import { useMutation, useQuery } from '@apollo/client';
import { Download, MoreHoriz, Router, Sell } from '@mui/icons-material';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { OutlinedIconButton } from '~/components/button';
import { SelectedCount } from '~/components/data-grid/SelectedCount';
import { ArbitraryCommandDialog } from '~/components/devices/ArbitraryCommandDialog';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { DeviceBulkSelectionInput, DevicesOrderBy } from '~/generated/graphql';
import { useRowSelectionMode } from '~/hooks/data-grid';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import {
  BrightnessDialog,
  PlayOverlayDialog,
  TuneChannelDialog,
  UpdateSoftwareDialog,
  VolumeDialog,
} from '../components';
import { ExportDevicesDocument } from '../queries/export.generated';
import { DeviceListDocument } from '../queries/list.generated';
import {
  BulkActions,
  BulkDeviceGroupDialog,
  ExportDialog,
  FilterAccordion,
  FilterChips,
  Grid,
  PlayContentDialog,
  ScheduleBulkAction,
  Table,
  TagsDialog,
} from './components';
import { BulkScheduleDialog } from './components/BulkScheduleDialog';
import { BulkDeviceSettingsDialog } from './components/BulkSettingsDialog';
import { BulkTagDialog } from './components/BulkTagDialog';
import type { DeviceActionHandler } from './components/CommandPopover';
import { StatusToggle } from './components/StatusToggle';
import { DeviceFiltersProvider, useDeviceFilters, useDeviceFiltersParams } from './context';
import { ActivateButton } from './lib';
import { generateScheduledShowName } from './lib/helpers';

type DialogSlot = { selected: DeviceBulkSelectionInput; onSuccess?: () => void };

const DeviceFilterSidebar = ({ children }: { children: ReactNode }) => {
  const { panel } = useDeviceFilters();
  return (
    <FilterSidebar open={panel.open} persistAs="deviceList:filterWidth">
      {children}
    </FilterSidebar>
  );
};

const DeviceFilterToggle = ({ active }: { active: boolean }) => {
  const { togglePanel } = useDeviceFilters();
  return <FilterToggle active={active} onToggle={togglePanel} />;
};

export const DeviceList = () => {
  const { t } = useTranslation();
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreMenuAnchorEl);

  const [brightnessDialog, setBrightnessDialog] = useState<DialogSlot | null>(null);
  const [volumeDialog, setVolumeDialog] = useState<DialogSlot | null>(null);
  const [tuneChannelDialog, setTuneChannelDialog] = useState<DialogSlot | null>(null);
  const [playContentDialog, setPlayContentDialog] = useState<DialogSlot | null>(null);
  const [playOverlayDialog, setPlayOverlayDialog] = useState<DialogSlot | null>(null);
  const [updateSoftwareDialog, setUpdateSoftwareDialog] = useState<DialogSlot | null>(null);
  const [arbitraryDialog, setArbitraryDialog] = useState<DialogSlot | null>(null);
  const [tagContentDialog, setTagContentDialog] = useState<DialogSlot | null>(null);
  const [addToGroupDialog, setAddToGroupDialog] = useState<DialogSlot | null>(null);
  const [bulkScheduleDialog, setBulkScheduleDialog] = useState<DialogSlot | null>(null);
  const [updateSettingsDialog, setUpdateSettingsDialog] = useState<DialogSlot | null>(null);

  const onBrightness: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setBrightnessDialog({ selected, onSuccess }),
    [],
  );
  const onVolume: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setVolumeDialog({ selected, onSuccess }),
    [],
  );
  const onTuneChannel: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setTuneChannelDialog({ selected, onSuccess }),
    [],
  );
  const onPlayContent: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setPlayContentDialog({ selected, onSuccess }),
    [],
  );
  const onPlayOverlay: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setPlayOverlayDialog({ selected, onSuccess }),
    [],
  );
  const onUpdateSoftware: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setUpdateSoftwareDialog({ selected, onSuccess }),
    [],
  );
  const onSendArbitrary: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setArbitraryDialog({ selected, onSuccess }),
    [],
  );
  const onTagContent: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setTagContentDialog({ selected, onSuccess }),
    [],
  );
  const onAddToGroup: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setAddToGroupDialog({ selected, onSuccess }),
    [],
  );
  const onScheduleShow: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setBulkScheduleDialog({ selected, onSuccess }),
    [],
  );
  const onUpdateSettings: DeviceActionHandler = useCallback(
    (selected, onSuccess) => setUpdateSettingsDialog({ selected, onSuccess }),
    [],
  );

  const { handleRowSelectionModeChange, rowSelectionMode, selectedIds, setSelectedIds } =
    useRowSelectionMode();

  const { currentNetwork } = useAppContext();

  const [exportDevices, { loading: exporting }] = useMutation(ExportDevicesDocument);

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const deviceView = searchParams.get('view')?.trim() || 'List';

  const filterParams = useDeviceFiltersParams();

  const condition = useMemo(
    () => ({ ...filterParams, networkId: currentNetwork.id, search }),
    [currentNetwork.id, filterParams, search],
  );

  const filtersActive = useMemo(
    () => Object.values(filterParams).some((v) => (Array.isArray(v) ? v.length > 0 : v != null)),
    [filterParams],
  );

  const { orderBy } = useOrderByParams<DevicesOrderBy>();
  const { page, perPage, handleOutOfBounds } = usePaginationParams();

  const { data, loading } = useQuery(DeviceListDocument, {
    variables: {
      condition,
      networkId: currentNetwork.id,
      ...(orderBy && { orderBy }),
      page,
      perPage,
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.devices.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.devices.pageInfo.nodesCount);

  const selectedDevices = useMemo(
    () => (rowSelectionMode === 'page' ? { ids: selectedIds.map(Number) } : { condition }),
    [condition, rowSelectionMode, selectedIds],
  );

  // Decide which bulk-action surface to show. The choice is user-level (role
  // config), not selection-level: same popover regardless of what's selected.
  //
  // Network managers (sysadmin / network owner) always get the full popover.
  // Users with create_scheduled_shows get the limited (schedule-only) popover,
  // since their selection may include devices they can only schedule on.
  //
  // Tradeoff: a user with both a write-access role AND create_scheduled_shows
  // will always see the limited popover here, even when selecting only their
  // writeable devices. Bulk-update on those is reachable via per-row actions.
  const showLimitedBulkActions =
    !currentNetwork.canManage.value && currentNetwork.canCreateScheduledShows.value;

  // reset selections when applying filters
  useEffect(() => {
    setSelectedIds([]);
  }, [setSelectedIds]);

  const scheduleNameSuggestion = useMemo(
    () =>
      generateScheduledShowName(
        data?.network?.allDeviceGroups.map((item) => ({ id: item.id, name: item.name })) ?? [],
        { ...filterParams, search },
      ),
    [data?.network?.allDeviceGroups, filterParams, search],
  );

  // Runtime device commands. Used by both the row popover and the bulk popover.
  const deviceActionHandlers = useMemo(
    () => ({
      onBrightness,
      onVolume,
      onTuneChannel,
      onPlayContent,
      onPlayOverlay,
      onUpdateSoftware,
      onSendArbitrary,
    }),
    [
      onBrightness,
      onVolume,
      onTuneChannel,
      onPlayContent,
      onPlayOverlay,
      onUpdateSoftware,
      onSendArbitrary,
    ],
  );

  // Adds tag, add-to-group, schedule, and update-settings on top of the
  // runtime commands. Used by the bulk popover only.
  const actionHandlers = useMemo(
    () => ({
      ...deviceActionHandlers,
      onTagContent,
      onAddToGroup,
      onScheduleShow,
      onUpdateSettings,
    }),
    [deviceActionHandlers, onTagContent, onAddToGroup, onScheduleShow, onUpdateSettings],
  );

  const clearBulkSelection = useCallback(() => setSelectedIds([]), [setSelectedIds]);

  const handleExport = useCallback(async () => {
    const result = await exportDevices({
      variables: {
        networkId: currentNetwork.id,
        condition,
      },
    });

    if (result.data?.exportDevices?.url) {
      setExportUrl(result.data.exportDevices.url);
      setExportDialogOpen(true);
    }
  }, [condition, currentNetwork.id, exportDevices]);

  const handleCloseExportDialog = useCallback(() => {
    setExportDialogOpen(false);
    setExportUrl(null);
  }, []);

  const handleMoreMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMoreMenuClose = useCallback(() => {
    setMoreMenuAnchorEl(null);
  }, []);

  const handleExportClick = useCallback(() => {
    handleMoreMenuClose();
    setExportDialogOpen(true);
  }, [handleMoreMenuClose]);

  const deviceFilterInfo = data?.network?.deviceFilterInfo;

  const allChannels = data?.network?.allChannels ?? [];
  const allDeviceGroups = data?.network?.allDeviceGroups ?? [];
  const allTvBrands = data?.network?.allTvBrands ?? [];
  const deviceTagNames = data?.network?.deviceTags.map((x) => x.name);

  return (
    <DeviceFiltersProvider
      deviceGroups={data?.network?.allDeviceGroups}
      deviceTags={deviceTagNames}
      displayServers={data?.network?.displayServers}
      models={data?.network?.models}
      params={filterParams}
      playerVersions={data?.network?.playerVersions}
      tvBrands={data?.network?.allTvBrands}
    >
      <Helmet title={t('common.labels.devices')} />
      {/* TODO: don't show for content editor, needs fix */}
      <Toolbar
        titleIcon={<Router />}
        titleText={t('common.labels.devices')}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setManageTagsOpen(!manageTagsOpen)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('common.tags.manage_tags', { defaultValue: 'Manage Tags' })}
            </Button>
            {currentNetwork.canActivateDevices.value && <ActivateButton />}
            {currentNetwork.canManage.value && (
              <>
                <OutlinedIconButton
                  label={t('common.buttons.more_actions')}
                  onClick={handleMoreMenuClick}
                  size="large"
                  variant="outlined"
                >
                  <MoreHoriz />
                </OutlinedIconButton>
                <Menu anchorEl={moreMenuAnchorEl} onClose={handleMoreMenuClose} open={moreMenuOpen}>
                  <MenuItem onClick={handleExportClick}>
                    <ListItemIcon>
                      <Download />
                    </ListItemIcon>
                    <ListItemText>{t('common.buttons.export')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        }
      />

      <ListPageContainer>
        <DeviceFilterSidebar>
          <FilterAccordion filterCounts={deviceFilterInfo} />
        </DeviceFilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
                width="100%"
                flexWrap="wrap"
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  flexWrap="wrap"
                >
                  <DeviceFilterToggle active={filtersActive} />
                  <SearchBar
                    onChange={(value) =>
                      setSearchParams((params) => {
                        params.set('search', value);
                        params.set('page', '1');
                        return params;
                      })
                    }
                    placeholder={t('pages.devices.search_placeholder')}
                    search={search}
                  />
                  <StatusToggle filterCounts={deviceFilterInfo} />
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  flexWrap="wrap"
                >
                  {selectedIds.length > 0 &&
                    (showLimitedBulkActions ? (
                      <ScheduleBulkAction
                        selected={selectedDevices}
                        totalCount={totalCount}
                        onScheduleShow={onScheduleShow}
                        onComplete={clearBulkSelection}
                      />
                    ) : (
                      <BulkActions
                        {...actionHandlers}
                        selected={selectedDevices}
                        totalCount={totalCount}
                        onComplete={clearBulkSelection}
                      />
                    ))}
                  <SelectedCount
                    rowSelectionMode={rowSelectionMode}
                    selectedCount={selectedIds.length}
                    thing="common.pluralize.device_plural"
                    totalCount={totalCount}
                  />
                </Stack>
              </Stack>
            </ListActions>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips />
              </Box>
              <ViewToggle
                view={deviceView as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {deviceView === 'List' ? (
                  <Table
                    devices={data?.network?.devices.nodes ?? []}
                    handleRowSelectionModeChange={handleRowSelectionModeChange}
                    loading={loading}
                    onCheck={setSelectedIds}
                    actionHandlers={deviceActionHandlers}
                    rowSelectionMode={rowSelectionMode}
                    selectedIds={selectedIds}
                  />
                ) : (
                  <Grid
                    devices={data?.network?.devices.nodes ?? []}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
          {brightnessDialog && (
            <BrightnessDialog
              onClose={() => setBrightnessDialog(null)}
              onComplete={() => {
                brightnessDialog.onSuccess?.();
                setBrightnessDialog(null);
              }}
              open
              selected={brightnessDialog.selected}
            />
          )}
          {volumeDialog && (
            <VolumeDialog
              onClose={() => setVolumeDialog(null)}
              onComplete={() => {
                volumeDialog.onSuccess?.();
                setVolumeDialog(null);
              }}
              open
              selected={volumeDialog.selected}
            />
          )}
          {tuneChannelDialog && (
            <TuneChannelDialog
              onClose={() => setTuneChannelDialog(null)}
              onComplete={() => {
                tuneChannelDialog.onSuccess?.();
                setTuneChannelDialog(null);
              }}
              open
              selected={tuneChannelDialog.selected}
            />
          )}
          {playContentDialog && (
            <PlayContentDialog
              onClose={() => setPlayContentDialog(null)}
              onSuccess={playContentDialog.onSuccess}
              open
              selected={playContentDialog.selected}
            />
          )}
          {playOverlayDialog && (
            <PlayOverlayDialog
              onClose={() => setPlayOverlayDialog(null)}
              onComplete={() => {
                playOverlayDialog.onSuccess?.();
                setPlayOverlayDialog(null);
              }}
              open
              selected={playOverlayDialog.selected}
            />
          )}
          {updateSoftwareDialog && (
            <UpdateSoftwareDialog
              onClose={() => setUpdateSoftwareDialog(null)}
              onComplete={() => {
                updateSoftwareDialog.onSuccess?.();
                setUpdateSoftwareDialog(null);
              }}
              selected={updateSoftwareDialog.selected}
            />
          )}
          {arbitraryDialog && (
            <ArbitraryCommandDialog
              onClose={() => setArbitraryDialog(null)}
              onComplete={() => {
                arbitraryDialog.onSuccess?.();
                setArbitraryDialog(null);
              }}
              open
              selected={arbitraryDialog.selected}
            />
          )}
          {tagContentDialog && (
            <BulkTagDialog
              deviceTags={[...(deviceTagNames ?? [])]}
              onClose={() => setTagContentDialog(null)}
              onComplete={() => {
                tagContentDialog.onSuccess?.();
                setTagContentDialog(null);
              }}
              open
              selected={tagContentDialog.selected}
            />
          )}
          {addToGroupDialog && (
            <BulkDeviceGroupDialog
              deviceGroups={allDeviceGroups}
              onClose={() => setAddToGroupDialog(null)}
              onComplete={() => {
                addToGroupDialog.onSuccess?.();
                setAddToGroupDialog(null);
              }}
              open
              selected={addToGroupDialog.selected}
            />
          )}
          {bulkScheduleDialog && (
            <BulkScheduleDialog
              name={scheduleNameSuggestion}
              onClose={() => setBulkScheduleDialog(null)}
              onSubmit={() => {
                bulkScheduleDialog.onSuccess?.();
                setBulkScheduleDialog(null);
              }}
              open
              selected={bulkScheduleDialog.selected}
            />
          )}
          {updateSettingsDialog && (
            <BulkDeviceSettingsDialog
              channels={allChannels}
              deviceGroups={allDeviceGroups}
              onClose={() => setUpdateSettingsDialog(null)}
              onComplete={() => {
                updateSettingsDialog.onSuccess?.();
                setUpdateSettingsDialog(null);
              }}
              open
              selected={updateSettingsDialog.selected}
              selectedDeviceCount={
                updateSettingsDialog.selected.ids
                  ? updateSettingsDialog.selected.ids.length
                  : totalCount
              }
              tvBrands={allTvBrands}
            />
          )}
          {manageTagsOpen && <TagsDialog onClose={() => setManageTagsOpen(false)} open />}

          {exportDialogOpen && (
            <ExportDialog
              close={handleCloseExportDialog}
              exportUrl={exportUrl}
              onExport={handleExport}
              exporting={exporting}
              totalCount={totalCount}
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </DeviceFiltersProvider>
  );
};
